import React from 'react';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '../../../../../theme';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import FilterCheckbox from './FilterCheckbox/FilterCheckbox';
import { useDispatch } from 'react-redux';
import { SeatMapRestrictions } from '@websky/graphql';
import Button from '../../../../../BaseComponents/Button';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import { TABLET_MIDDLE_WIDTH } from '../../../../../utils';
import FullscreenDialog from '../../../../../FullscreenDialog';
import { filterIcons } from './icons';
import { clearPassengerFilters, setPassengerFilter } from '../../../../store/actions';
import { CdnIcon } from '@shared/ui';
import { AllowedSeatMapFilters } from '../../../../types';

interface Props {
	isActive?: boolean;
	allowedFilters: AllowedSeatMapFilters[];
	activeFilters: SeatMapRestrictions[];
	passengerId: string;
	passengerName: string;
	filteredSeatsCount: number;
}

const PassengerFilters: React.FC<Props> = ({
	isActive,
	passengerId,
	allowedFilters,
	activeFilters,
	passengerName,
	filteredSeatsCount
}) => {
	const theme = useTheme('SeatMap').PassengerFilters;
	const { t } = useTranslation('SeatMap');
	const dispatch = useDispatch();

	const isTablet = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });

	const [isShown, setShown] = React.useState(false);

	const onToggleCollapse = () => setShown(prev => !prev);

	const onSelectFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
		const restriction = event.target.value as SeatMapRestrictions;

		dispatch(setPassengerFilter(restriction));
	};

	const onClearFilters = () => {
		dispatch(clearPassengerFilters());
	};

	const onCloseModal = () => {
		onClearFilters();
		onToggleCollapse();
	};

	React.useEffect(() => {
		if (!isTablet) {
			setShown(isActive);
		}

		return () => {
			if (isActive) {
				onClearFilters();
			}
		};
	}, [isActive]);

	const renderCaption = () => <p className={theme.caption}>{t('Remove seats prohibited for')}:</p>;

	const renderAllowedFilters = () =>
		allowedFilters.map(filter => {
			const isChecked = activeFilters?.includes(filter.title);

			return (
				<FilterCheckbox
					key={`${passengerId}_${filter.title}`}
					title={filter.title}
					onChange={onSelectFilter}
					checked={isChecked}
					disabled={!filter.isEffective && !isChecked}
				/>
			);
		});

	const renderClearButton = () => (
		<Button
			variant="text"
			classes={{ root: theme.controls__clear }}
			disableTouchRipple={true}
			onClick={onClearFilters}
		>
			{t('Clear')}
		</Button>
	);

	const renderSeatCount = () =>
		!!filteredSeatsCount && <span className={theme.seats_count}>{t('seat', { count: filteredSeatsCount })}</span>;

	return (
		<div className={theme.filters}>
			<MediaQuery minWidth={TABLET_MIDDLE_WIDTH + 1}>
				<div className={theme.title} onClick={onToggleCollapse}>
					{t(isShown ? 'Hide filters' : 'Show filters')}:
					<span
						className={cn(theme.arrow, {
							[theme.arrow_turned]: isShown
						})}
					>
						<CdnIcon icon="Arrow/Expand_more" />
					</span>
				</div>

				<Collapse in={isShown} className={theme.collapse}>
					{renderCaption()}

					<div className={theme.container}>{renderAllowedFilters()}</div>

					<div className={theme.controls}>
						{renderClearButton()}

						{renderSeatCount()}
					</div>
				</Collapse>
			</MediaQuery>

			<MediaQuery maxWidth={TABLET_MIDDLE_WIDTH}>
				<div className={theme.title} onClick={onToggleCollapse}>
					<span className={theme.title__icon}>{filterIcons.titleIcon}</span>

					{t('Show filters')}
				</div>

				<FullscreenDialog
					isOpen={isShown}
					title={passengerName}
					onClose={onCloseModal}
					contentClassName={theme.modal_content}
					closeClassName={theme.close__icon}
				>
					{renderCaption()}

					<div className={theme.container}>{renderAllowedFilters()}</div>

					<div className={theme.controls}>
						{renderClearButton()}

						<div>
							{renderSeatCount()}

							<Button variant="action" classes={{ root: theme.show_button }} onClick={onToggleCollapse}>
								{t('Show')}
							</Button>
						</div>
					</div>
				</FullscreenDialog>
			</MediaQuery>
		</div>
	);
};

export default PassengerFilters;
